import { Dispatch, ListenerEffect } from '@reduxjs/toolkit';
import { ChatConnectionDetails, ChatMessageBasic, ChatMessageStatus } from 'domain/Chat';
import { selectUserAccountId } from 'user/api';

import { addMessages, excludeFromMessages, markMessagesAsRejected, setChatConnectionDetails } from './chatSlice';

import type { RootState } from 'store';

type Effect<T> = ListenerEffect<{ type: string; payload: T }, RootState, Dispatch>;

export const newMessagesEffect: Effect<ChatMessageBasic[]> = ({ payload }, { dispatch, getState }) => {
  const state = getState();
  const accountId = selectUserAccountId(state);
  dispatch(addMessages({ messages: payload, status: ChatMessageStatus.Approved, accountId }));
};

export const newPendingMessageEffect: Effect<ChatMessageBasic> = ({ payload }, { dispatch, getState }) => {
  const state = getState();
  const accountId = selectUserAccountId(state);

  dispatch(addMessages({ messages: [payload], status: ChatMessageStatus.Pending, accountId }));
};

/**
 * Approval reaches the host and the sender (the two clients holding a pending copy) as two separate
 * WS messages with no guaranteed order: `MessagesApproved` (this effect) and `ChatMessages`
 * (`newMessagesEffect`). `addMany` is a no-op for an id already in the store, so handling this frame
 * as a bare removal only worked in one order. Approved copy first, approval notice second left the
 * pending copy untouched and then deleted it, and the message vanished from the sender's chat for
 * good — the moderated-chat E2E caught exactly that.
 *
 * So drop the pending copy AND re-add the approved one from this payload, which carries the whole
 * message. Either order now converges on one approved message at the end of the list: whichever
 * frame lands second is a no-op, because `addMany` ignores an id the first one already inserted.
 * Both dispatches are synchronous here, so React 18 batches them into one render.
 */
export const newApprovedMessagesEffect: Effect<ChatMessageBasic[]> = ({ payload }, { dispatch, getState }) => {
  const accountId = selectUserAccountId(getState());

  dispatch(excludeFromMessages({ messages: payload }));
  dispatch(addMessages({ messages: payload, status: ChatMessageStatus.Approved, accountId }));
};

export const newRejectedMessagesEffect: Effect<ChatMessageBasic[]> = ({ payload }, { dispatch, getState }) => {
  const state = getState();
  const accountId = selectUserAccountId(state);

  const myMessages = payload.filter(({ sender }) => sender.id === accountId);
  dispatch(markMessagesAsRejected({ messages: myMessages }));

  const otherMessages = payload.filter(({ sender }) => sender.id !== accountId);
  dispatch(excludeFromMessages({ messages: otherMessages }));
};

export const connectionDetailsEffect: Effect<ChatConnectionDetails> = ({ payload }, { dispatch }) => {
  dispatch(setChatConnectionDetails(payload));
};
